/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, Modal, Text, Types } from '@marriott/mi-ui-library';
import { EXTERNAL_CTA_TYPE, INTERNAL_CTA_TYPE } from '../../constants/CommonConstants';
import { decodeHtml } from '../../utils/OfferUtils';
import { TermsAndConditionsModalProps, TnCLinksWithModalProps } from './TnCLinksWithModal.types';
import { StyledTnCLinksWithModal } from './TnCLinksWithModal.styles';

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = props => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Accessibility as per WAI-ARIA standards
  useEffect(() => {
    // Save the currently focused element
    const previouslyFocusedElement = document.activeElement as HTMLElement;

    // Focus the modal when it opens
    if (modalRef.current) {
      const firstFocusableElement = modalRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])' //these are the focusable elements
      ) as HTMLElement;
      firstFocusableElement?.focus();
    }

    // Handle keyboard events
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.toggleModal(event as any);
        return;
      }

      // Focus trap: handle Tab key (Last Element to First Element)
      if (event.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey) {
          // Shift + Tab: if on first element, go to last
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          // Tab: if on last element, go to first
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Cleanup: restore focus when modal closes
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [props]);

  return (
    <div ref={modalRef}>
      <Modal
        show={true}
        role={'dialog'}
        aria-modal="true"
        aria-labelledby={props?.labelledBy}
        modalId={props?.labelledBy}
      >
        <Modal.Header labelText={props?.labelText} popupHeaderOnCLoseFunc={(event: any) => props?.toggleModal(event)} />
        <Modal.Body>
          <div tabIndex={0} role="document" aria-label={props?.labelledBy}>
            <Text
              fontSize={Types.size.medium}
              element={Types.tags.paragraph}
              dangerouslySetInnerHTML={{
                __html: ` ${props?.details !== null ? decodeHtml(props?.details) : ''}`,
              }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export const TnCLinksWithModal: React.FC<TnCLinksWithModalProps> = props => {
  const { tncCta, trackingProperties } = props;
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null);

  const toggleModal = (event: React.MouseEvent, index: number | null) => {
    const body = document.querySelector('body');
    if (body) {
      if (index !== null) {
        event.preventDefault();
        body.style.overflow = 'hidden';
        body.style.paddingRight = `15px`;
      } else {
        body.style.overflow = '';
        body.style.paddingRight = '';
      }
    }
    setOpenModalIndex(prev => (prev === index ? null : index));
  };

  const validLinks =
    tncCta?.filter(tncData => {
      if (!tncData?.label) return false;
      if (tncData?.extInd && !tncData?.link) return false;
      return true;
    }) || [];

  return (
    <StyledTnCLinksWithModal className={props?.className ?? ''}>
      <div className={`d-flex link-wrapper ${props?.wrapperClassName ?? ''}`}>
        {validLinks.map((tncData, index) => {
          const isExternal = !!tncData?.extInd;
          return (
            <React.Fragment key={tncData?.label + index}>
              {index > 0 && <div className="link-divider" />}
              <span className="d-flex align-items-center">
                <Link
                  text={tncData?.label}
                  ariaLabel={`${tncData?.label}-${index}-link`}
                  linkClassName={`${isExternal ? 'm-link-tertiary-button-external' : 'm-link'}`}
                  linkHref={isExternal ? tncData.link : '#'}
                  target={isExternal ? '_blank' : '_self'}
                  linkType={isExternal ? 'external' : 'internal'}
                  trackingProperties={trackingProperties}
                  custom_click_track_value={`${trackingProperties?.cardLocation}|${tncData.label}|${
                    isExternal ? EXTERNAL_CTA_TYPE : INTERNAL_CTA_TYPE
                  }`}
                  callback={
                    !isExternal && tncData?.details
                      ? event => {
                          event.preventDefault();
                          toggleModal(event, index);
                        }
                      : undefined
                  }
                />
                {!isExternal &&
                  tncData?.details &&
                  openModalIndex === index &&
                  typeof document !== 'undefined' &&
                  createPortal(
                    <TermsAndConditionsModal
                      labelText={tncData?.label}
                      details={tncData?.details}
                      toggleModal={(event: React.MouseEvent) => toggleModal(event, null)}
                      labelledBy={`${tncData?.label}-${index}-link`}
                    />,
                    document.body
                  )}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </StyledTnCLinksWithModal>
  );
};

export default TnCLinksWithModal;
